import { type Session } from "@decentriq/core";
import { exceptions } from "@decentriq/utils";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { type parameter_payloads as ddcParameterPayloads } from "ddc";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  MediaDataRoomJobInput,
  useMediaDataRoomLazyJob,
} from "features/mediaDataRoom/hooks/ab";
import {
  type AudienceAb,
  type PublishedDatasetsHashes,
} from "features/mediaDataRoom/models";
import {
  AudienceSizeEstimationError,
  type AudienceSizesResult,
} from "../models";
import useAudienceSizesState from "../useAudienceSizesState";
import { transformEstimatedAudienceSize } from "../utils";

interface AudienceSizesHookPayload {
  dataRoomId: string;
  driverAttestationHash: string;
  audiences: AudienceAb[] | undefined;
  getParameterPayloads: (
    audienceId: string,
    audiences: AudienceAb[]
  ) => ddcParameterPayloads.ParameterPayloads;
  session: Session | undefined;
  getScopeId: () => Promise<string>;
  isPublisher?: boolean;
  publishedDatasetsHashes: PublishedDatasetsHashes;
}

type AudienceSizesHookResult = AudienceSizesResult;

export const useAudienceSizes = ({
  dataRoomId,
  driverAttestationHash,
  audiences,
  session,
  getScopeId,
  getParameterPayloads,
  publishedDatasetsHashes,
  isPublisher,
}: AudienceSizesHookPayload): AudienceSizesHookResult => {
  const queryClient = useQueryClient();
  const skipCachingRef = useRef<Record<string, boolean>>({});
  const [enableAudienceSizeEstimation, setEnableAudienceSizeEstimation] =
    useState<string[]>([]);
  const [estimateAudienceSizeForAdvertiser] = useMediaDataRoomLazyJob({
    getScopeId,
    input: MediaDataRoomJobInput.create(
      "estimateAudienceSizeForAdvertiser",
      dataRoomId,
      driverAttestationHash,
      publishedDatasetsHashes
    ),
    session,
    transform: transformEstimatedAudienceSize,
  });
  const [estimateAudienceSizeForAdvertiserLal] = useMediaDataRoomLazyJob({
    getScopeId,
    input: MediaDataRoomJobInput.create(
      "estimateAudienceSizeForAdvertiserLal",
      dataRoomId,
      driverAttestationHash,
      publishedDatasetsHashes
    ),
    session,
    transform: transformEstimatedAudienceSize,
  });
  const [estimateAudienceSizeForPublisher] = useMediaDataRoomLazyJob({
    getScopeId,
    input: MediaDataRoomJobInput.create(
      "estimateAudienceSizeForPublisher",
      dataRoomId,
      driverAttestationHash,
      publishedDatasetsHashes
    ),
    session,
    transform: transformEstimatedAudienceSize,
  });
  const [estimateAudienceSizeForPublisherLal] = useMediaDataRoomLazyJob({
    getScopeId,
    input: MediaDataRoomJobInput.create(
      "estimateAudienceSizeForPublisherLal",
      dataRoomId,
      driverAttestationHash,
      publishedDatasetsHashes
    ),
    session,
    transform: transformEstimatedAudienceSize,
  });
  const buildQueryKey = useCallback(
    (audienceId: string) => [
      "ab",
      "v0",
      dataRoomId,
      "driverAttestationHash",
      driverAttestationHash,
      "datasets",
      publishedDatasetsHashes.datasetsHash,
      isPublisher
        ? "estimateAudienceSizeForPublisher"
        : "estimateAudienceSizeForAdvertiser",
      audienceId,
    ],
    [
      dataRoomId,
      driverAttestationHash,
      isPublisher,
      publishedDatasetsHashes.datasetsHash,
    ]
  );
  const handleFetch = useCallback(
    async (id: string) => {
      try {
        const payloadParams = getParameterPayloads(id, audiences!);
        let size: number;
        if (payloadParams.lal) {
          size = await (
            isPublisher
              ? estimateAudienceSizeForPublisherLal
              : estimateAudienceSizeForAdvertiserLal
          )({
            requestCreator: (dataRoomIdHex, scopeIdHex) => ({
              dataRoomIdHex,
              generateAudience: payloadParams.generate,
              lalAudience: payloadParams.lal!,
              scopeIdHex,
            }),
            updateInput: (input) => {
              const updatedInput = skipCachingRef.current[id]
                ? input.withResourceId(id).withoutCaching()
                : input.withResourceId(id);
              skipCachingRef.current = {
                ...skipCachingRef.current,
                [id]: false,
              };
              return updatedInput;
            },
          });
        } else {
          size = await (
            isPublisher
              ? estimateAudienceSizeForPublisher
              : estimateAudienceSizeForAdvertiser
          )({
            requestCreator: (dataRoomIdHex, scopeIdHex) => ({
              dataRoomIdHex,
              generateAudience: payloadParams.generate,
              scopeIdHex,
            }),
            updateInput: (input) => {
              const updatedInput = skipCachingRef.current[id]
                ? input.withResourceId(id).withoutCaching()
                : input.withResourceId(id);
              skipCachingRef.current = {
                ...skipCachingRef.current,
                [id]: false,
              };
              return updatedInput;
            },
          });
        }
        return { [id]: size };
      } catch (error) {
        throw new AudienceSizeEstimationError(
          `Failed to estimate audience size for audience ${id}`,
          id,
          error
        );
      }
    },
    [
      audiences,
      estimateAudienceSizeForAdvertiser,
      estimateAudienceSizeForAdvertiserLal,
      estimateAudienceSizeForPublisher,
      estimateAudienceSizeForPublisherLal,
      getParameterPayloads,
      isPublisher,
    ]
  );
  const audienceSizes = useQueries({
    queries: enableAudienceSizeEstimation.map((id) => ({
      enabled:
        publishedDatasetsHashes.hasRequiredData &&
        audiences?.some((a) => a.id === id),
      queryFn: async () => await handleFetch(id),
      queryKey: buildQueryKey(id),
      retry: (failureCount: number, error: Error) => {
        if (
          failureCount >= 3 ||
          !(error instanceof AudienceSizeEstimationError)
        ) {
          return false;
        }
        const audienceId = error.audienceId;
        if (
          error.originalError instanceof
          exceptions.MediaDataRoomOutdatedAudiencesNodeError
        ) {
          skipCachingRef.current = {
            ...skipCachingRef.current,
            [audienceId]: true,
          };
          return true;
        }
        return false;
      },
    })),
  });
  const state = useAudienceSizesState({
    audienceSizes,
    audiences,
    enableAudienceSizeEstimation,
    setEnableAudienceSizeEstimation,
  });
  const retrySizeEstimationForAudience = useCallback(
    async (audienceId: string) => {
      const queryKey = buildQueryKey(audienceId);
      skipCachingRef.current = {
        ...skipCachingRef.current,
        [audienceId]: true,
      };
      await queryClient.refetchQueries({
        queryKey,
      });
    },
    [buildQueryKey, queryClient]
  );
  return useMemo(
    () => ({
      ...state,
      retrySizeEstimationForAudience,
    }),
    [state, retrySizeEstimationForAudience]
  );
};
